import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import toast from "react-hot-toast";
import moment from "moment";
import GameSessionSeriveces from "../../services/GameSessionSeriveces";
import Loader from "../../components/Loader";
import NoData from "../../components/NoData";
import "../../styles/SessionWinningDetails.css";
import { LIVE_YANTRA_IMAGE_URL } from "../../utils/utils";

const formatMoney = (v) => {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const SessionWinningDetails = () => {
  const api = useMemo(() => new GameSessionSeriveces(), []);
  const routerHistory = useHistory();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const getSessionId = () => {
    try {
      return window.sessionStorage.getItem("swd_session_id");
    } catch (_) {
      return null;
    }
  };

  const fetchDetail = async () => {
    const sid = getSessionId();
    if (!sid) {
      setData(null);
      return;
    }

    setLoading(true);
    try {
      const res = await api.getSessionWinningDetail(sid);
      if (res?.isSuccess) {
        setData(res.data);
      } else {
        toast.error(res?.message || "Failed to load session detail");
        setData(null);
      }
    } catch (err) {
      console.error("getSessionWinningDetail error:", err);
      toast.error("Failed to load session detail");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <Loader message="Loading session winning detail..." />;
  if (!data)
    return (
      <div className="swd-page">
        <div className="swd-topbar">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => routerHistory.goBack()}
          >
            Back
          </button>
        </div>
        <NoData
          message="Select a session to view details"
          subText="Open Yantra Sessions page and click the preview icon."
        />
      </div>
    );

  const session = data.session || {};
  const statistics = data.statistics || {};
  const yantras = data.yantras?.list || [];
  const grandTotal = Number(data.yantras?.grand_total || 0);

  const winners = new Set(
    (data.highlight?.winner_yantra_ids || []).map(Number),
  );

  return (
    <div className="swd-page">
      {/* Session information */}
      <div className="swd-card swd-session-card">
        <div className="swd-card-header">
          <div className="swd-card-title">
            Session Information (
            <span className="swd-subtitle">
              #{session.session_no} • {session.game_type}
            </span>
            )
          </div>
          <div
            className={`swd-badge swd-badge-${String(session.status || "").toLowerCase()}`}
          >
            {session.status || "-"}
          </div>
          <div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => routerHistory.goBack()}
              >
                Back
              </button>
              <button className="btn btn-primary btn-sm" onClick={fetchDetail}>
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="swd-kv-grid">
          <div className="swd-kv">
            <div className="swd-k">Session UID({session.game_type || "-"})</div>
            <div className="swd-v">
              {session.session_uid || "-"}(#{session.session_no ?? "-"})
            </div>
          </div>
          <div className="swd-kv">
            <div className="swd-k">Start Time</div>
            <div className="swd-v">
              {session.start_time
                ? moment
                    .utc(session.start_time)
                    .local()
                    .format("DD MMM YYYY, hh:mm A")
                : "-"}{" "}
              -{" "}
              {session.end_time
                ? moment.utc(session.end_time).local().format("hh:mm A")
                : "-"}
            </div>
          </div>

          <div className="swd-kv">
            <div className="swd-k">Winner Type</div>
            <div className="swd-v">
              {session.winner_type === "JACKPOTX"
                ? `Jackpot (${session.winner_x})`
                : session.winner_type === "JACKPOT"
                  ? "Multi Winner"
                  : session.winner_type || "-"}
            </div>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="swd-section">
        <div className="swd-section-title">Session Statistics</div>
        <div className="swd-stats-grid">
          <div className="swd-stat">
            <div className="swd-stat-label">Total Participants</div>
            <div className="swd-stat-value">
              {statistics.total_participants ?? 0}
            </div>
          </div>
          <div className="swd-stat">
            <div className="swd-stat-label">Total Place Bets</div>
            <div className="swd-stat-value">
              {statistics.total_place_bets ?? 0}
            </div>
          </div>

          <div className="swd-stat">
            <div className="swd-stat-label">
              Percentage Amount({session.session_percentage ?? 0}%)
            </div>
            <div className="swd-stat-value">
              ₹{formatMoney(statistics.percentage_amount)}
            </div>
          </div>
          <div className="swd-stat">
            <div className="swd-stat-label">Winning Amount</div>
            <div className="swd-stat-value">
              ₹{formatMoney(statistics.winning_amount)}
            </div>
          </div>
          <div className="swd-stat">
            <div className="swd-stat-label">Winning Amount Ratio</div>
            <div className="swd-stat-value">
              ₹{formatMoney(statistics?.winning_amount_ratio?.min)} - ₹
              {formatMoney(statistics?.winning_amount_ratio?.max)}
            </div>
          </div>
        </div>
      </div>

      {/* Yantra list */}
      <div className="swd-section">
        {yantras.length === 0 ? (
          <NoData message="No yantra data found" />
        ) : (
          <>
            {/* Desktop / tablet: table view */}
            <div className="swd-yantra-table-wrap">
              <table className="swd-yantra-table">
                <thead>
                  <tr>
                    <th style={{ width: "5%" }}>Sr.</th>
                    <th style={{ width: "25%", textAlign: "left" }}>Yantra</th>
                    <th className="swd-th-right">Buy Count</th>
                    <th className="swd-th-right">Yantra Amount</th>
                    <th className="swd-th-right">Total Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {yantras.map((y, index) => {
                    const isWinner =
                      winners.has(Number(y.yantra_id)) || !!y.is_winner;
                    const imgSrc = y.yantra_img
                      ? `${LIVE_YANTRA_IMAGE_URL}/${y.yantra_img}`
                      : `${LIVE_YANTRA_IMAGE_URL}/default.png`;

                    return (
                      <tr
                        key={y.yantra_id}
                        className={isWinner ? "swd-row-winner" : ""}
                      >
                        <td className="text-center">{index + 1}</td>
                        <td style={{ textAlign: "left" }}>
                          <div className="swd-yantra-cell">
                            <div className="swd-yantra-img-wrap">
                              <img
                                className="swd-yantra-img"
                                src={imgSrc}
                                alt={y.yantra_name || "yantra"}
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = `${LIVE_YANTRA_IMAGE_URL}/default.png`;
                                }}
                              />
                            </div>
                            <div className="swd-yantra-text">
                              <div className="swd-yantra-name">
                                {y.yantra_name || `Yantra #${y.yantra_id}`}
                                <span>
                                  {isWinner && (
                                    <span className="swd-pill swd-pill-winner">
                                      WINNER
                                    </span>
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="swd-td-right">
                          {y.total_buy_count ?? 0}
                        </td>
                        <td className="swd-td-right">
                          ₹{formatMoney(y.yantra_amount)}
                        </td>
                        <td className="swd-td-right">
                          ₹{formatMoney(y.total_amount)}
                        </td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td colSpan={5} className="swd-td-grand-total">
                      <div className="swd-grand-total swd-grand-total--table">
                        <span className="swd-grand-total-label">
                          Grand Total
                        </span>
                        <span className="swd-grand-total-value">
                          ₹{formatMoney(grandTotal)}
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Mobile: card view */}
            <div className="swd-yantra-cards">
              {yantras.map((y) => {
                const isWinner =
                  winners.has(Number(y.yantra_id)) || !!y.is_winner;
                const imgSrc = y.yantra_img
                  ? `${LIVE_YANTRA_IMAGE_URL}/${y.yantra_img}`
                  : `${LIVE_YANTRA_IMAGE_URL}/default.png`;

                return (
                  <div
                    key={y.yantra_id}
                    className={`swd-yantra-card ${isWinner ? "swd-yantra-winner" : ""}`}
                  >
                    <div className="swd-yantra-top">
                      <div className="swd-yantra-img-wrap">
                        <img
                          className="swd-yantra-img"
                          src={imgSrc}
                          alt={y.yantra_name || "yantra"}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = `${LIVE_YANTRA_IMAGE_URL}/default.png`;
                          }}
                        />
                        <div className="swd-yantra-img-fallback">YANTRA</div>
                      </div>
                      <div className="swd-yantra-name">
                        {y.yantra_name || `Yantra #${y.yantra_id}`}
                      </div>
                      {isWinner ? (
                        <div className="swd-winner-pill">WINNER</div>
                      ) : null}
                    </div>

                    <div className="swd-yantra-metrics">
                      <div className="swd-metric">
                        <div className="swd-metric-label">Total Buy Count</div>
                        <div className="swd-metric-value">
                          {y.total_buy_count ?? 0}
                        </div>
                      </div>
                      <div className="swd-metric">
                        <div className="swd-metric-label">Yantra Amount</div>
                        <div className="swd-metric-value">
                          ₹{formatMoney(y.yantra_amount)}
                        </div>
                      </div>
                      <div className="swd-metric">
                        <div className="swd-metric-label">Total Amount</div>
                        <div className="swd-metric-value">
                          ₹{formatMoney(y.total_amount)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SessionWinningDetails;
